import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import {
  parseTrustRuleKey,
  type AutonomyEvent,
  type AutonomyScorecardDay,
  type GuidanceEntry,
  type IngestTriageConfig,
  type KindUsefulness,
  type ParkedIngest,
  type ProducerId,
  type RoutingRule,
  type TriageMode,
  type TrustRule,
} from '@lapo/core';

/**
 * Autonomy panel (AXIS X-06/X-07 — spec §6): the passive review surface for everything Lapo does on
 * its own. Four stacked sections:
 *  - the MASTER SWITCH (X22): the per-account autonomy flag; off = kill switch (every rule reads
 *    confirm; Lapo-owned Gmail filters are removed asynchronously),
 *  - the DAILY RECAP (X4): "here's everything I did on my own" with per-item + bulk undo,
 *  - the COMPOSITE SCORECARD (X19): intervention rate ↓ · correct-autonomy ↑ · throughput,
 *  - the RULES LEDGER (X14): two tabs for the two rule families — Action rules (TrustRule: policy,
 *    streak/strike state, provenance, reset/delete) and Routing rules (RoutingRule: loudness,
 *    priority, batch) — plus the global downstream-target defaults (X27).
 * No black box: every learned rule is visible, editable, resettable (X14).
 */

/** The apiClient slice this panel needs. */
export interface AutonomyApi {
  /** X7 — one-shot cold-start over past Gmail behavior; everything it yields is a proposal. */
  runColdStart(): Promise<{ proposals: string[]; fewShotSeeds: number; scannedMessages: number }>;
  listTrustRules(): Promise<TrustRule[]>;
  setTrustRule(ruleKey: string, policy: TrustRule['policy'], target?: TrustRule['target']): Promise<unknown>;
  removeTrustRule(ruleKey: string): Promise<void>;
  resetTrustRule(ruleKey: string): Promise<void>;
  listRoutingRules(): Promise<RoutingRule[]>;
  setRoutingRule(rule: Partial<RoutingRule> & { ruleKey: string }): Promise<unknown>;
  removeRoutingRule(ruleKey: string): Promise<void>;
  getAutonomyRecap(dateKey?: string): Promise<AutonomyEvent[]>;
  getAutonomyScorecard(from?: string, to?: string): Promise<AutonomyScorecardDay[]>;
  undoAutonomyEvent(eventId: string): Promise<boolean>;
  undoAutonomyDay(dateKey: string): Promise<{ undone: number; failed: number }>;
  // SIEVE (ingest-triage.md S6-S11) — the Filtered section.
  listParkedIngest(): Promise<ParkedIngest[]>;
  restoreParkedIngest(parkedId: string): Promise<boolean>;
  getIngestTriageScorecard(): Promise<{ parks: number; restores: number; precision: number }>;
  // TUNE (signal-feedback.md U6/U8/U10) — guidance ledger, usefulness scores, recap quality votes.
  listGuidance(): Promise<GuidanceEntry[]>;
  putGuidance(input: { entryId?: string; producerId: ProducerId; kind: 'rule' | 'fewshot'; text: string }): Promise<GuidanceEntry>;
  deleteGuidance(entryId: string): Promise<void>;
  approveGuidance(entryId: string): Promise<void>;
  getFeedbackScores(): Promise<KindUsefulness[]>;
  recordAutonomyFeedback(eventId: string, quality: 'good' | 'not-good', note?: string): Promise<boolean>;
}

export interface AutonomyPanelProps {
  api: AutonomyApi | null;
  /** The user's local today (YYYY-MM-DD) — the recap day. */
  today: string;
  /** The master autonomy flag (X22), owned by the app's profile state. */
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  /** Global downstream-target defaults (X27), owned by the app's profile state. */
  actionDefaults: { todoistProjectId?: string; taskLabel?: string; calendarId?: string };
  onCommitActionDefaults: (defaults: { todoistProjectId?: string; taskLabel?: string; calendarId?: string }) => void;
  /** SIEVE S10 — per-source ingest-triage modes, owned by the app's profile state. */
  ingestTriage: IngestTriageConfig;
  onCommitIngestTriage: (cfg: IngestTriageConfig) => void;
}

const TRIAGE_MODES: TriageMode[] = ['off', 'shadow', 'enforce'];

const POLICIES: Array<TrustRule['policy']> = ['confirm', 'gated', 'auto', 'escalate'];

function ruleLabel(rule: TrustRule): string {
  const parsed = parseTrustRuleKey(rule.ruleKey);
  if (!parsed) return rule.ruleKey;
  const scope = parsed.grain === 'category' ? `all ${parsed.subject}` : parsed.subject;
  return `${parsed.disposition} · ${scope}`;
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

export function AutonomyPanel({
  api,
  today,
  enabled,
  onToggleEnabled,
  actionDefaults,
  onCommitActionDefaults,
  ingestTriage,
  onCommitIngestTriage,
}: AutonomyPanelProps): ReactElement {
  const [parked, setParked] = useState<ParkedIngest[]>([]);
  const [sieveScore, setSieveScore] = useState<{ parks: number; restores: number; precision: number } | null>(null);
  const [guidance, setGuidance] = useState<GuidanceEntry[]>([]);
  const [scores, setScores] = useState<KindUsefulness[]>([]);
  const [newGuidance, setNewGuidance] = useState<{ producerId: ProducerId; text: string }>({ producerId: 'email-triage', text: '' });
  const [rules, setRules] = useState<TrustRule[]>([]);
  const [routing, setRouting] = useState<RoutingRule[]>([]);
  const [recap, setRecap] = useState<AutonomyEvent[]>([]);
  const [days, setDays] = useState<AutonomyScorecardDay[]>([]);
  const [tab, setTab] = useState<'action' | 'routing'>('action');
  const [note, setNote] = useState<string | null>(null);
  const [defaultsDraft, setDefaultsDraft] = useState(actionDefaults);
  useEffect(() => setDefaultsDraft(actionDefaults), [actionDefaults]);

  const load = useCallback(async () => {
    if (!api) return;
    const [r, rt, rec, sc, pk, ss, gd, fs] = await Promise.all([
      api.listTrustRules().catch(() => []),
      api.listRoutingRules().catch(() => []),
      api.getAutonomyRecap(today).catch(() => []),
      api.getAutonomyScorecard().catch(() => []),
      api.listParkedIngest().catch(() => []),
      api.getIngestTriageScorecard().catch(() => null),
      api.listGuidance().catch(() => []),
      api.getFeedbackScores().catch(() => []),
    ]);
    setRules(r);
    setRouting(rt);
    setRecap(rec);
    setDays(sc);
    setParked(pk);
    setSieveScore(ss);
    setGuidance(gd);
    setScores(fs);
  }, [api, today]);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = useCallback((text: string) => {
    setNote(text);
    setTimeout(() => setNote(null), 4000);
  }, []);

  const setPolicy = useCallback(
    async (rule: TrustRule, policy: TrustRule['policy']) => {
      if (!api) return;
      await api.setTrustRule(rule.ruleKey, policy);
      void load();
    },
    [api, load],
  );

  const undoOne = useCallback(
    async (eventId: string) => {
      if (!api) return;
      const ok = await api.undoAutonomyEvent(eventId);
      flash(ok ? 'Undone — and Lapo takes the hint.' : "Couldn't undo that one.");
      void load();
    },
    [api, flash, load],
  );

  const undoAll = useCallback(async () => {
    if (!api) return;
    const res = await api.undoAutonomyDay(today);
    flash(`Undid ${res.undone} ${res.undone === 1 ? 'action' : 'actions'}${res.failed ? ` (${res.failed} failed)` : ''}.`);
    void load();
  }, [api, flash, load, today]);

  const latest = days[days.length - 1];
  const executed = recap.filter((e) => e.outcome === 'executed');

  return (
    <div className="lapo-autonomy" data-testid="autonomy-panel">
      {note && (
        <div className="lapo-signals-undo" role="status">
          {note}
        </div>
      )}

      {/* ── Master switch (X22) ─────────────────────────────────────────────── */}
      <section className="lapo-autonomy-section">
        <header className="lapo-autonomy-head">
          <MaterialIcon name="auto_awesome" size={16} />
          <h3>Autonomy</h3>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label="Autonomy master switch"
            data-testid="autonomy-flag"
            className={`lapo-toggle${enabled ? ' is-on' : ''}`}
            onClick={() => onToggleEnabled(!enabled)}
          >
            <span className="lapo-toggle-knob" />
          </button>
        </header>
        <p className="lapo-autonomy-hint">
          {enabled
            ? 'Rules you’ve graduated act on their own. Turning this off instantly makes every rule ask first and removes Lapo’s Gmail filters — earned trust is kept for when you turn it back on.'
            : 'Off — Lapo prepares everything and always asks. Earned trust is preserved.'}
        </p>
        <button
          type="button"
          className="lapo-set-btn"
          data-testid="autonomy-cold-start"
          title="Analyze your past Gmail behavior (bounded, local to your account) and propose starting rules — nothing applies without your yes"
          onClick={() =>
            void api
              ?.runColdStart()
              .then((r) =>
                flash(
                  r.proposals.length
                    ? `Proposed ${r.proposals.length} ${r.proposals.length === 1 ? 'rule' : 'rules'} from your history — they’ll appear as cards to accept.`
                    : 'Looked through your history — nothing new to propose.',
                ),
              )
              .then(load)
              .catch(() => flash("Couldn't analyze your history right now."))
          }
        >
          Learn from my history
        </button>
      </section>

      {/* ── Daily recap + bulk undo (X4) ────────────────────────────────────── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-recap">
        <header className="lapo-autonomy-head">
          <h4>Today, on its own</h4>
          {executed.length > 1 && (
            <button type="button" className="lapo-set-btn" data-testid="autonomy-undo-all" onClick={() => void undoAll()}>
              Undo all
            </button>
          )}
        </header>
        {recap.length === 0 ? (
          <p className="lapo-autonomy-hint">Nothing yet — auto-handled actions land here for review.</p>
        ) : (
          <ul className="lapo-autonomy-list">
            {recap.map((e) => (
              <li key={e.eventId} data-testid={`recap-${e.eventId}`}>
                <span className="lapo-autonomy-title">{e.title ?? `${e.disposition} · ${e.ruleKey}`}</span>
                {e.outcome === 'undone' ? (
                  <span className="lapo-set-muted">undone</span>
                ) : (
                  <span className="lapo-autonomy-rowactions">
                    {/* TUNE U8 — the quality vote: good = clean streak, not-good = a strike (no undo). */}
                    {e.qualityFeedback ? (
                      <span className="lapo-set-muted">{e.qualityFeedback === 'good' ? 'good ✓' : 'noted ✗'}</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="lapo-set-btn"
                          aria-label={`Good: ${e.title ?? e.eventId}`}
                          data-testid={`recap-good-${e.eventId}`}
                          onClick={() => void api?.recordAutonomyFeedback(e.eventId, 'good').then(() => load())}
                        >
                          Good
                        </button>
                        <button
                          type="button"
                          className="lapo-set-btn"
                          aria-label={`Not good: ${e.title ?? e.eventId}`}
                          data-testid={`recap-bad-${e.eventId}`}
                          onClick={() => void api?.recordAutonomyFeedback(e.eventId, 'not-good').then(() => load())}
                        >
                          Not good
                        </button>
                      </>
                    )}
                    {e.reversible && e.undoToken ? (
                      <button type="button" className="lapo-set-btn" onClick={() => void undoOne(e.eventId)}>
                        Undo
                      </button>
                    ) : null}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── SIEVE — Filtered shared items (ingest-triage.md S6-S11) ─────────── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-filtered">
        <header className="lapo-autonomy-head">
          <h4>Filtered</h4>
          {sieveScore && sieveScore.parks > 0 && (
            <span className="lapo-set-muted" data-testid="sieve-precision" title="1 − restores/parks over enforced parks — restores are precision misses">
              {pct(sieveScore.precision)} precision · {sieveScore.parks} filtered · {sieveScore.restores} restored
            </span>
          )}
        </header>
        <div className="lapo-autonomy-triage-modes">
          {(
            [
              ['Instagram shares', 'instagram'],
              ['Email inbox', 'emailInbox'],
            ] as const
          ).map(([label, key]) => (
            <label key={key} className="lapo-autonomy-triage-mode">
              <span>{label}</span>
              <select
                aria-label={`${label} triage mode`}
                data-testid={`sieve-mode-${key}`}
                value={ingestTriage[key]}
                onChange={(e) => onCommitIngestTriage({ ...ingestTriage, [key]: e.target.value as TriageMode })}
              >
                {TRIAGE_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m === 'off' ? 'Off' : m === 'shadow' ? 'Shadow (judge only)' : 'Enforce'}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        {parked.length === 0 ? (
          <p className="lapo-autonomy-hint">Nothing filtered — parked shares land here, restorable for 90 days.</p>
        ) : (
          <ul className="lapo-autonomy-list">
            {parked.map((row) => (
              <li key={row.parkedId} data-testid={`parked-${row.parkedId}`}>
                <span className="lapo-autonomy-title" title={row.reason}>
                  {row.title}
                </span>
                <button
                  type="button"
                  className="lapo-set-btn"
                  onClick={() =>
                    void api?.restoreParkedIngest(row.parkedId).then((ok) => {
                      if (ok) {
                        setParked((prev) => prev.filter((r) => r.parkedId !== row.parkedId));
                        flash('Restoring — it will reappear in your journal shortly.');
                      }
                    })
                  }
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Composite scorecard (X19) ───────────────────────────────────────── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-scorecard">
        <h4>Scorecard</h4>
        {latest ? (
          <div className="lapo-autonomy-scores">
            <div title="Fraction of items that needed a tap — the point is for this to fall">
              <strong data-testid="score-intervention">{pct(latest.interventionRate)}</strong>
              <span>needed you</span>
            </div>
            <div title="Of the actions Lapo took on its own, how many you did NOT undo — the guardrail">
              <strong data-testid="score-correct">{pct(latest.correctAutonomyRate)}</strong>
              <span>auto-actions correct</span>
            </div>
            <div title="Items cleared today">
              <strong data-testid="score-throughput">{latest.clearedCount}</strong>
              <span>cleared</span>
            </div>
            {latest.suppressedCount > 0 && (
              <div title="Signals hidden by your routing rules — visible so suppression can't fake progress">
                <strong data-testid="score-suppressed">{latest.suppressedCount}</strong>
                <span>suppressed</span>
              </div>
            )}
          </div>
        ) : (
          <p className="lapo-autonomy-hint">No activity yet.</p>
        )}
      </section>

      {/* ── TUNE — Guidance Ledger (U6): every learned/manual instruction, visible + editable ── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-guidance">
        <header className="lapo-autonomy-head">
          <h4>Guidance</h4>
          <span className="lapo-set-muted">What Lapo has learned from your feedback — edit or remove anything</span>
        </header>
        {guidance.filter((g) => g.status !== 'retired').length === 0 ? (
          <p className="lapo-autonomy-hint">Nothing yet — “Teach Lapo…” on any card, or add a standing rule below.</p>
        ) : (
          <ul className="lapo-autonomy-list">
            {guidance
              .filter((g) => g.status !== 'retired')
              .map((g) => (
                <li key={g.entryId} data-testid={`guidance-${g.entryId}`}>
                  <span className="lapo-autonomy-title" title={`${g.origin} · ${g.kind}`}>
                    <em className="lapo-set-muted">[{g.producerId}]</em> {g.text}
                  </span>
                  {g.status === 'proposed' && (
                    <button
                      type="button"
                      className="lapo-set-btn"
                      data-testid={`guidance-approve-${g.entryId}`}
                      onClick={() => void api?.approveGuidance(g.entryId).then(() => load())}
                    >
                      Approve
                    </button>
                  )}
                  <button
                    type="button"
                    className="lapo-set-btn"
                    aria-label={`Delete guidance ${g.entryId}`}
                    onClick={() => void api?.deleteGuidance(g.entryId).then(() => load())}
                  >
                    Delete
                  </button>
                </li>
              ))}
          </ul>
        )}
        <div className="lapo-autonomy-guidance-add">
          <select
            aria-label="Guidance area"
            value={newGuidance.producerId}
            onChange={(e) => setNewGuidance((g) => ({ ...g, producerId: e.target.value as ProducerId }))}
          >
            <option value="email-triage">Email triage</option>
            <option value="slack-digest">Slack digest</option>
            <option value="sieve">Share filtering</option>
            <option value="echo">Resurfacing</option>
            <option value="ledger">Decisions</option>
            <option value="engines">Capture engines</option>
          </select>
          <input
            type="text"
            aria-label="New guidance rule"
            data-testid="guidance-new-text"
            placeholder="Add a standing rule, e.g. “Never escalate invoices from ACME”"
            value={newGuidance.text}
            onChange={(e) => setNewGuidance((g) => ({ ...g, text: e.target.value }))}
          />
          <button
            type="button"
            className="lapo-set-btn"
            data-testid="guidance-add"
            disabled={!newGuidance.text.trim()}
            onClick={() =>
              void api
                ?.putGuidance({ producerId: newGuidance.producerId, kind: 'rule', text: newGuidance.text.trim() })
                .then(() => {
                  setNewGuidance((g) => ({ ...g, text: '' }));
                  return load();
                })
            }
          >
            Add
          </button>
        </div>
      </section>

      {/* ── TUNE — usefulness per kind (U10): the dashboard that shows refinement working ── */}
      {scores.length > 0 && (
        <section className="lapo-autonomy-section" data-testid="autonomy-usefulness">
          <h4>Signal usefulness (30d)</h4>
          <ul className="lapo-autonomy-list">
            {scores.slice(0, 8).map((k) => (
              <li key={k.kind}>
                <span className="lapo-autonomy-title">{k.kind}</span>
                <span className="lapo-set-muted" data-testid={`usefulness-${k.kind}`}>
                  {pct(k.score)} useful · {k.useful + k.notUseful} votes
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Rules ledger (X14) — two tabs for the two rule families ─────────── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-ledger">
        <header className="lapo-autonomy-head">
          <h4>Rules</h4>
          <div className="lapo-autonomy-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'action'} onClick={() => setTab('action')}>
              Actions
            </button>
            <button type="button" role="tab" aria-selected={tab === 'routing'} onClick={() => setTab('routing')}>
              Routing
            </button>
          </div>
        </header>
        {tab === 'action' &&
          (rules.length === 0 ? (
            <p className="lapo-autonomy-hint">No rules yet — teach Lapo from any card’s ⋯ menu.</p>
          ) : (
            <ul className="lapo-autonomy-list">
              {rules.map((r) => (
                <li key={r.ruleKey} data-testid={`rule-${r.ruleKey}`}>
                  <span className="lapo-autonomy-title" title={r.ruleKey}>
                    {ruleLabel(r)}
                    {(r.gmailFilterId || Object.keys(r.gmailFilterIds ?? {}).length > 0) && (
                      <span className="lapo-set-chip" title="Backed by a Lapo-owned Gmail filter in the mailbox(es) this sender mails">
                        Gmail filter
                      </span>
                    )}
                  </span>
                  <span className="lapo-set-muted" title="clean streak / distinct days / strikes in window">
                    {r.cleanStreak}✓ · {r.distinctDays}d{r.strikes.length ? ` · ${r.strikes.length}⚠` : ''} · {r.source}
                  </span>
                  <select
                    aria-label={`Policy for ${r.ruleKey}`}
                    value={r.policy}
                    onChange={(e) => void setPolicy(r, e.target.value as TrustRule['policy'])}
                  >
                    {POLICIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="lapo-set-btn"
                    title="Clear the learned streak/strikes"
                    onClick={() => void (api?.resetTrustRule(r.ruleKey).then(load) ?? null)}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="lapo-set-btn"
                    aria-label={`Delete rule ${r.ruleKey}`}
                    onClick={() => void (api?.removeTrustRule(r.ruleKey).then(load) ?? null)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ))}
        {tab === 'routing' &&
          (routing.length === 0 ? (
            <p className="lapo-autonomy-hint">No routing rules — tune loudness/priority from any card’s ⋯ menu.</p>
          ) : (
            <ul className="lapo-autonomy-list">
              {routing.map((r) => (
                <li key={r.ruleKey} data-testid={`routing-${r.ruleKey}`}>
                  <span className="lapo-autonomy-title">{r.ruleKey}</span>
                  <select
                    aria-label={`Loudness for ${r.ruleKey}`}
                    value={r.loudness ?? 'loud'}
                    onChange={(e) =>
                      void (api
                        ?.setRoutingRule({ ruleKey: r.ruleKey, loudness: e.target.value as RoutingRule['loudness'] })
                        .then(load) ?? null)
                    }
                  >
                    <option value="loud">loud</option>
                    <option value="quiet">quiet</option>
                    <option value="suppress">never show</option>
                  </select>
                  <select
                    aria-label={`Priority for ${r.ruleKey}`}
                    value={r.priorityOverride ?? ''}
                    onChange={(e) =>
                      void (api
                        ?.setRoutingRule({
                          ruleKey: r.ruleKey,
                          priorityOverride: (e.target.value || undefined) as RoutingRule['priorityOverride'],
                        })
                        .then(load) ?? null)
                    }
                  >
                    <option value="">(no override)</option>
                    <option value="urgent">urgent</option>
                    <option value="action">action</option>
                    <option value="info">info</option>
                  </select>
                  <button
                    type="button"
                    className="lapo-set-btn"
                    aria-label={`Delete routing ${r.ruleKey}`}
                    onClick={() => void (api?.removeRoutingRule(r.ruleKey).then(load) ?? null)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          ))}
      </section>

      {/* ── Global downstream-target defaults (X27) ─────────────────────────── */}
      <section className="lapo-autonomy-section" data-testid="autonomy-defaults">
        <h4>Where actions land</h4>
        <p className="lapo-autonomy-hint">Defaults for tasks Lapo creates; a rule can override per sender.</p>
        <div className="lapo-autonomy-defaults">
          <input
            type="text"
            className="lapo-set-input"
            aria-label="Default Todoist project"
            placeholder="Todoist project id"
            value={defaultsDraft.todoistProjectId ?? ''}
            onChange={(e) => setDefaultsDraft({ ...defaultsDraft, todoistProjectId: e.target.value || undefined })}
            onBlur={() => onCommitActionDefaults(defaultsDraft)}
          />
          <input
            type="text"
            className="lapo-set-input"
            aria-label="Task label"
            placeholder="Task label (default @lapo)"
            value={defaultsDraft.taskLabel ?? ''}
            onChange={(e) => setDefaultsDraft({ ...defaultsDraft, taskLabel: e.target.value || undefined })}
            onBlur={() => onCommitActionDefaults(defaultsDraft)}
          />
          <input
            type="text"
            className="lapo-set-input"
            aria-label="Default calendar"
            placeholder="Calendar id"
            value={defaultsDraft.calendarId ?? ''}
            onChange={(e) => setDefaultsDraft({ ...defaultsDraft, calendarId: e.target.value || undefined })}
            onBlur={() => onCommitActionDefaults(defaultsDraft)}
          />
        </div>
      </section>
    </div>
  );
}
